import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { CONTROL_CENTRE_API_DOMAIN } from '@/config/constants'
import useAuthenticatedMutation from '@/hooks/useAuthenticatedMutation'
import { cn } from '@/utils/cn'
import type { AdminClientItem } from '@/types/controlCenter'
import { QUERY_KEYS } from '../utils/queryKeys'
import { labelClass, inputClass } from '../components/shared/modalStyles'
import ModalShell from '../components/shared/ModalShell'

interface ClientModalProps {
  client?: AdminClientItem | null
  onClose: () => void
}

const ClientModal = ({ client, onClose }: ClientModalProps) => {
  const queryClient = useQueryClient()
  const isCreate = !client

  const [name, setName] = useState(client?.name ?? '')
  const [description, setDescription] = useState(client?.description ?? '')

  const mutation = useAuthenticatedMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.clients,
      })
      onClose()
    },
  })

  const handleSave = () => {
    mutation.mutate({
      method: isCreate ? 'post' : 'put',
      url: isCreate
        ? `${CONTROL_CENTRE_API_DOMAIN}/admin/clients`
        : `${CONTROL_CENTRE_API_DOMAIN}/admin/clients/${client.id}`,
      data: {
        name: name.trim(),
        description: description.trim(),
      },
    })
  }

  return (
    <ModalShell
      title={isCreate ? 'Create Client' : 'Edit Client'}
      subtitle={!isCreate ? client.name : undefined}
      onClose={onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSave()
        }}
        className='max-h-[65vh] space-y-5 overflow-y-auto px-6 py-5'
      >
        <div>
          <label htmlFor='client-name' className={labelClass}>
            Name *
          </label>
          <input
            id='client-name'
            required
            aria-required='true'
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Client name'
          />
        </div>
        <div>
          <label htmlFor='client-description' className={labelClass}>
            Description
          </label>
          <textarea
            id='client-description'
            className={cn(inputClass, 'resize-none')}
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='Brief description of this client'
          />
        </div>

        {mutation.error && (
          <p className='text-sm text-red-400'>{mutation.error.message}</p>
        )}

        <div className='border-border-main flex items-center justify-end gap-3 border-t px-6 py-4'>
          <button
            type='button'
            onClick={onClose}
            className='border-border-main text-text-secondary hover:bg-neutral-dimmed rounded-lg border px-4 py-2 text-sm'
          >
            Cancel
          </button>
          <button
            type='submit'
            disabled={name.trim() === '' || mutation.isPending}
            className='rounded-lg bg-violet-600 px-5 py-2 text-sm font-semibold text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-40'
          >
            {mutation.isPending
              ? 'Saving…'
              : isCreate
                ? 'Create Client'
                : 'Save'}
          </button>
        </div>
      </form>
    </ModalShell>
  )
}

export default ClientModal
